import type { HelpTopic } from '../types';

export const contractorsTopics: HelpTopic[] = [
  {
    id: 'wms.contractors.create',
    title: 'Create a Contractor',
    icon: 'ri-user-star-line',
    summary: 'Register a new contractor in the master list so they can be awarded tenders.',
    sections: [
      {
        id: 'steps',
        title: 'Steps',
        body: (
          <ol className="list-decimal list-inside space-y-1 text-sm text-gray-700">
            <li>Contractors page → <span className="font-medium">New Contractor</span>.</li>
            <li>Enter firm details, category, PAN / GST, registration number.</li>
            <li>Add primary contact (phone, email).</li>
            <li>Add bank details used for payments.</li>
            <li>Attach mandatory documents (registration, PAN card, GST, IT returns).</li>
            <li>Save — status starts as <span className="font-medium">ACTIVE</span>.</li>
          </ol>
        )
      }
    ],
    related: ['wms.contractor-users.create', 'wms.contractors.assign-in-award'],
    pageLinks: [
      { label: 'Open Contractors', to: '/app/ds-wms/contractors', icon: 'ri-user-star-line' }
    ],
    launchForms: [
      { label: 'New Contractor', icon: 'ri-user-add-line', formKey: 'ContractorForm' }
    ]
  },
  {
    id: 'wms.contractor-users.create',
    title: 'Create Contractor Users',
    icon: 'ri-user-settings-line',
    summary: 'Contractor users log in to the vendor portal to track works, submit progress, and raise bills.',
    sections: [
      {
        id: 'from-contractor-profile',
        title: 'From a contractor profile',
        body: (
          <ol className="list-decimal list-inside space-y-1 text-sm text-gray-700">
            <li>Contractors page → click a contractor to open the profile.</li>
            <li>Users tab → <span className="font-medium">Add User</span>.</li>
            <li>Enter login id, name, role (owner / staff), mobile + email.</li>
            <li>Set a temporary password — user must reset on first login.</li>
          </ol>
        )
      },
      {
        id: 'from-users-page',
        title: 'From the Contractor Users page',
        body: (
          <p className="text-sm text-gray-700">
            Same form, but you pick the contractor up-front. Useful when provisioning many users across firms in one go.
          </p>
        )
      },
      {
        id: 'roles',
        title: 'Roles',
        body: (
          <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
            <li><span className="font-medium">Owner</span> — full access to all firm works, can create/manage staff users.</li>
            <li><span className="font-medium">Staff</span> — can only see works assigned to them.</li>
            <li><span className="font-medium">Viewer</span> — read-only.</li>
          </ul>
        )
      }
    ],
    pageLinks: [
      { label: 'Open Contractors', to: '/app/ds-wms/contractors', icon: 'ri-user-star-line', hint: 'Open a contractor → Users tab' }
    ]
  },
  {
    id: 'wms.contractors.assign-in-award',
    title: 'Assign Contractor in Award screen',
    icon: 'ri-shake-hands-line',
    summary: 'Bind the awarded tender to a registered contractor; this is what unlocks the vendor portal for that work.',
    sections: [
      {
        id: 'steps',
        title: 'Steps',
        body: (
          <ol className="list-decimal list-inside space-y-1 text-sm text-gray-700">
            <li>Open the tender in the Award Decision step.</li>
            <li>Click <span className="font-medium">Select Contractor</span> — search by code / name / category.</li>
            <li>Confirm; contact + bank details autofill.</li>
            <li>Enter Award Amount, LOA number + date, Agreement number + date.</li>
            <li>Save — the contractor is linked to every work in the package.</li>
          </ol>
        )
      },
      {
        id: 'missing-contractor',
        title: "Contractor not in the list?",
        body: (
          <p className="text-sm text-gray-700">
            Use <span className="font-medium">Create Contractor</span> on the Contractors page, then return to the award
            step. The form will not save without a registered contractor.
          </p>
        )
      }
    ],
    related: ['wms.contractors.create', 'wms.tenders.award'],
    pageLinks: [
      { label: 'Open Tenders', to: '/app/ds-wms/tenders', icon: 'ri-auction-line', hint: 'Go to Award Decision step' },
      { label: 'Tender Execution', to: '/app/ds-wms/tender-execution', icon: 'ri-play-circle-line' }
    ]
  },
  {
    id: 'wms.contractors.users-from-profile',
    title: 'Create Contractor Users (from profile)',
    icon: 'ri-user-add-line',
    summary: 'Shortcut to add a login user straight from a contractor profile page.',
    sections: [
      {
        id: 'steps',
        title: 'Steps',
        body: (
          <ol className="list-decimal list-inside space-y-1 text-sm text-gray-700">
            <li>Open the contractor's profile.</li>
            <li>Switch to the <span className="font-medium">Users</span> tab.</li>
            <li>Click <span className="font-medium">Add User</span> and fill the form.</li>
          </ol>
        )
      }
    ],
    related: ['wms.contractor-users.create'],
    pageLinks: [
      { label: 'Open Contractors', to: '/app/ds-wms/contractors', icon: 'ri-user-star-line' }
    ]
  }
];
